import cronstrue from 'cronstrue';
import 'cronstrue/locales/pt_BR';
import { getContact } from '../../api/contacts';
import {
  CONTENT_GENERATION_MODE_LABELS,
  DEFAULT_RECURRENCE_CRON,
  DEFAULT_RECURRENCE_DAYS,
  DEFAULT_RECURRENCE_HOUR,
  DEFAULT_RECURRENCE_MINUTE,
  RECURRENCE_DAY_PRESETS,
  RECURRENCE_DAYS_FILTER_PREFIX,
  RECURRENCE_PRESET_FILTER_OPTIONS,
  SCHEDULE_TYPE_LABELS,
  TIMEZONE_OPTIONS,
} from './constants';

const CRON_FALLBACK = 'Agenda recorrente';

export function formatDateTime(iso) {
  if (!iso) return '—';
  return new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(new Date(iso));
}

export function toIsoFromDatetimeLocal(value) {
  if (!value) return null;
  return new Date(value).toISOString();
}

export function toDatetimeLocalFromIso(iso) {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

export function isScheduledMessageEditable(status) {
  return status === 'pending' || status === 'failed';
}

export function isScheduledMessageCancellable(status) {
  return status === 'pending' || status === 'awaiting_window';
}

export function isScheduledMessageDeletable(status) {
  return status !== 'processing';
}

export function isScheduledMessageRetryable(status, updatedAt) {
  if (status !== 'processing' || !updatedAt) return false;
  const ageMs = Date.now() - new Date(updatedAt).getTime();
  return ageMs >= 12 * 60 * 60 * 1000;
}

export function isScheduledMessageResendable(item) {
  return item?.status === 'failed' && Boolean(item?.can_resend);
}

export function isScheduledMessageForceDispatchable(item) {
  return item?.status === 'pending'
    && item?.content_generation_mode === 'runtime'
    && item?.generation_status !== 'generating'
    && Boolean(item?.can_force_dispatch);
}

export function isScheduledMessageTemplateSendable(item) {
  return (item?.status === 'pending' || item?.status === 'failed')
    && item?.generation_status !== 'generating'
    && Boolean(item?.can_send_template);
}

export function isScheduledMessageOverdue(item) {
  if (!item?.next_run_at || item.status !== 'pending') {
    return false;
  }
  return new Date(item.next_run_at) < new Date();
}

export function getScheduledMessageDownloadableAttachments(item) {
  return Array.isArray(item?.downloadable_attachments)
    ? item.downloadable_attachments.filter((attachment) => attachment?.download_url)
    : [];
}

export function buildScheduledMessagesEditPath(mode, { agentId, scheduledMessageId } = {}) {
  const base = mode === 'admin' ? '/admin/scheduled-messages' : '/scheduled-messages';
  const query = agentId ? `?agentId=${agentId}` : '';
  return `${base}/${scheduledMessageId}/edit${query}`;
}

function isNumericCronField(value) {
  return /^\d+$/.test(String(value).trim());
}

function expandDaysOfWeek(daysOfWeek) {
  if (!daysOfWeek || daysOfWeek === '*') {
    return '*';
  }

  if (/^\d-\d$/.test(daysOfWeek)) {
    const [start, end] = daysOfWeek.split('-').map(Number);
    if (!Number.isNaN(start) && !Number.isNaN(end) && start <= end) {
      return Array.from({ length: end - start + 1 }, (_, index) => String(start + index)).join(',');
    }
  }

  return daysOfWeek;
}

function normalizeDaysOfWeek(daysOfWeek) {
  const expanded = expandDaysOfWeek(daysOfWeek);
  if (!expanded || expanded === '*') {
    return '*';
  }

  if (expanded.includes('-')) {
    return expanded;
  }

  const uniqueDays = [...new Set(
    expanded
      .split(',')
      .map((day) => day.trim())
      .filter(Boolean)
  )];

  uniqueDays.sort((left, right) => {
    const leftValue = left === '0' ? 7 : Number(left);
    const rightValue = right === '0' ? 7 : Number(right);
    return leftValue - rightValue;
  });

  return uniqueDays.join(',');
}

function getRecurrenceDayPreset(daysOfWeek) {
  const normalizedDays = normalizeDaysOfWeek(daysOfWeek);
  const preset = RECURRENCE_DAY_PRESETS.find(
    (option) => option.value !== 'custom' && option.value === normalizedDays
  );
  return preset?.value || 'custom';
}

export function buildCronFromVisual({ minute, hour, daysOfWeek }) {
  const normalizedDays = normalizeDaysOfWeek(daysOfWeek);
  return `${minute} ${hour} * * ${normalizedDays}`;
}

export function parseCronToVisual(cron) {
  const fallback = {
    isVisualCompatible: false,
    minute: DEFAULT_RECURRENCE_MINUTE,
    hour: DEFAULT_RECURRENCE_HOUR,
    daysOfWeek: DEFAULT_RECURRENCE_DAYS,
    dayPreset: DEFAULT_RECURRENCE_DAYS,
  };

  if (!cron?.trim()) {
    return {
      ...fallback,
      isVisualCompatible: true,
    };
  }

  const parts = cron.trim().split(/\s+/);
  if (parts.length !== 5) {
    return fallback;
  }

  const [minute, hour, dayOfMonth, month, daysOfWeek] = parts;
  if (!isNumericCronField(minute) || !isNumericCronField(hour)) {
    return fallback;
  }
  if (dayOfMonth !== '*' || month !== '*') {
    return fallback;
  }

  const normalizedDays = normalizeDaysOfWeek(daysOfWeek);
  return {
    isVisualCompatible: true,
    minute,
    hour,
    daysOfWeek: normalizedDays,
    dayPreset: getRecurrenceDayPreset(normalizedDays),
  };
}

export function formatRecurrenceDaysLabel(daysOfWeek) {
  const preset = RECURRENCE_DAY_PRESETS.find(
    (option) => option.value !== 'custom' && option.value === normalizeDaysOfWeek(daysOfWeek)
  );
  if (preset) {
    return preset.label;
  }

  if (!daysOfWeek || daysOfWeek === '*') {
    return 'Todos os dias';
  }

  return 'Dias personalizados';
}

export function buildDefaultRecurrenceFormFields() {
  return {
    scheduleRecurrenceMode: 'visual',
    recurrenceMinute: DEFAULT_RECURRENCE_MINUTE,
    recurrenceHour: DEFAULT_RECURRENCE_HOUR,
    recurrenceDays: DEFAULT_RECURRENCE_DAYS,
    recurrenceDayPreset: DEFAULT_RECURRENCE_DAYS,
    cron: DEFAULT_RECURRENCE_CRON,
  };
}

export function resolveFormCron(form) {
  if (form.scheduleType !== 'recurring') {
    return '';
  }

  if (form.scheduleRecurrenceMode === 'visual') {
    return buildCronFromVisual({
      minute: form.recurrenceMinute,
      hour: form.recurrenceHour,
      daysOfWeek: form.recurrenceDays,
    });
  }

  return form.cron?.trim() || '';
}

export function isValidRecurrenceForm(form) {
  if (form.scheduleType !== 'recurring') {
    return true;
  }

  if (form.scheduleRecurrenceMode === 'advanced') {
    return isValidCronExpression(form.cron);
  }

  if (!form.recurrenceHour || form.recurrenceMinute === '') {
    return false;
  }

  const days = normalizeDaysOfWeek(form.recurrenceDays);
  if (!days || days === 'custom') {
    return false;
  }

  return isValidCronExpression(
    buildCronFromVisual({
      minute: form.recurrenceMinute,
      hour: form.recurrenceHour,
      daysOfWeek: days,
    })
  );
}

export function formStateFromScheduledMessage(item) {
  const baseState = {
    templateName: item.template_name || '',
    generationInstruction: item.generation_instruction || '',
    contentGenerationMode: item.content_generation_mode || 'runtime',
    batchLeadDays: String(item.batch_lead_days ?? 1),
    scheduleType: item.schedule_type || 'one_off',
    scheduledAt: toDatetimeLocalFromIso(item.scheduled_at),
    cron: item.cron || '',
    timezone: item.timezone || 'UTC',
    endsAt: toDatetimeLocalFromIso(item.ends_at),
    ...buildDefaultRecurrenceFormFields(),
  };

  if (item.schedule_type !== 'recurring' || !item.cron) {
    return baseState;
  }

  const parsed = parseCronToVisual(item.cron);
  if (!parsed.isVisualCompatible) {
    return {
      ...baseState,
      scheduleRecurrenceMode: 'advanced',
      cron: item.cron,
    };
  }

  return {
    ...baseState,
    scheduleRecurrenceMode: 'visual',
    recurrenceMinute: parsed.minute,
    recurrenceHour: parsed.hour,
    recurrenceDays: parsed.daysOfWeek,
    recurrenceDayPreset: parsed.dayPreset,
    cron: buildCronFromVisual({
      minute: parsed.minute,
      hour: parsed.hour,
      daysOfWeek: parsed.daysOfWeek,
    }),
  };
}

export function buildSchedulePayload(form) {
  const payload = {
    template_name: form.templateName.trim(),
    generation_instruction: form.generationInstruction.trim(),
    content_generation_mode: form.contentGenerationMode,
    batch_lead_days: Number(form.batchLeadDays) || 1,
    schedule_type: form.scheduleType,
    timezone: form.timezone || 'UTC',
    template_variables: {},
  };

  if (form.scheduleType === 'one_off') {
    payload.scheduled_at = toIsoFromDatetimeLocal(form.scheduledAt);
  } else {
    payload.cron = resolveFormCron(form);
    if (form.endsAt) {
      payload.ends_at = toIsoFromDatetimeLocal(form.endsAt);
    }
  }

  return payload;
}

export function formatCronDescription(cron) {
  if (!cron?.trim()) {
    return CRON_FALLBACK;
  }

  try {
    return cronstrue.toString(cron.trim(), {
      locale: 'pt_BR',
      use24HourTimeFormat: true,
    });
  } catch {
    return CRON_FALLBACK;
  }
}

export function isValidCronExpression(cron) {
  if (!cron?.trim()) {
    return false;
  }

  try {
    cronstrue.toString(cron.trim(), {
      locale: 'pt_BR',
      use24HourTimeFormat: true,
    });
    return true;
  } catch {
    return false;
  }
}

export function formatTimezoneLabel(timezone) {
  const tz = timezone || 'UTC';
  const option = TIMEZONE_OPTIONS.find((item) => item.value === tz);
  return option?.label || tz;
}

export function buildRecurringScheduleSummary(item) {
  const description = formatCronDescription(item.cron);
  const timezone = formatTimezoneLabel(item.timezone);
  return `${description}, ${timezone}`;
}

export function buildScheduleSummary(item) {
  if (item.schedule_type === 'one_off') {
    return formatDateTime(item.scheduled_at);
  }
  return buildRecurringScheduleSummary(item);
}

export function buildScheduledMessagesPath(mode, { agentId, scheduledMessageId } = {}) {
  const base = mode === 'admin' ? '/admin/scheduled-messages' : '/scheduled-messages';
  const query = agentId ? `?agentId=${agentId}` : '';
  if (scheduledMessageId) {
    return `${base}/${scheduledMessageId}${query}`;
  }
  if (agentId) {
    return `${base}?agentId=${agentId}`;
  }
  return base;
}

export function buildListQueryString(agentId, status, contact) {
  const params = new URLSearchParams();
  if (agentId) {
    params.set('agentId', agentId);
  }
  if (status) {
    params.set('status', status);
  }
  if (contact) {
    params.set('contact', contact);
  }
  const query = params.toString();
  return query ? `?${query}` : '';
}

export function formatGenerationMode(mode) {
  return CONTENT_GENERATION_MODE_LABELS[mode] || mode;
}

export function formatScheduleType(type) {
  return SCHEDULE_TYPE_LABELS[type] || type;
}

export function contactLabel(item, contactsById) {
  if (item.contact?.external_id) {
    return item.contact.external_id;
  }
  if (contactsById?.[item.contact_id]?.external_id) {
    return contactsById[item.contact_id].external_id;
  }
  return '—';
}

export async function loadContactsForItems(items, existingMap = {}) {
  const map = { ...existingMap };
  const contactIds = [
    ...new Set(
      (items || [])
        .map((item) => item.contact_id)
        .filter(Boolean)
    ),
  ].filter((contactId) => !map[contactId]?.external_id);

  await Promise.all(
    contactIds.map(async (contactId) => {
      try {
        const contact = await getContact(contactId);
        map[contact.id] = contact;
      } catch {
        // Contact may be deleted or inaccessible.
      }
    })
  );

  return map;
}

export function shouldPollItems(items) {
  return items.some(
    (item) =>
      item.status === 'pending' ||
      item.status === 'awaiting_window' ||
      item.status === 'processing' ||
      item.generation_status === 'generating' ||
      item.generation_status === 'pending'
  );
}

export function buildUserStatusHint(item) {
  if (!item) return '';

  const { status, content_generation_mode: generationMode, generation_status: generationStatus } =
    item;
  const isBatch = generationMode === 'batch';

  if (isBatch && generationStatus === 'generating') {
    return 'Gerando conteúdo antes do envio.';
  }

  if (isBatch && generationStatus === 'pending') {
    return 'O conteúdo será gerado antes do envio.';
  }

  if (isBatch && generationStatus === 'failed') {
    return 'Falha ao gerar o conteúdo. Verifique o erro abaixo.';
  }

  if (status === 'awaiting_window') {
    return 'Aguardando o contato interagir no WhatsApp.';
  }

  if (status === 'failed') {
    if (item.can_resend) {
      return 'Envio falhou. Você pode reenviar a mensagem.';
    }
    return 'Envio falhou.';
  }

  if (status === 'pending') {
    if (isBatch && generationStatus === 'ready') {
      if (item.can_send_template) {
        return 'Conteúdo pronto. Você pode enviar o template para abrir a janela do WhatsApp.';
      }
      return 'Conteúdo pronto. Aguardando o horário de envio.';
    }
    if (!isBatch && item.can_force_dispatch) {
      return 'Aguardando o horário de envio. Você pode gerar e enviar agora.';
    }
    if (item.can_send_template) {
      return 'Aguardando o horário de envio. Você pode enviar o template para abrir a janela do WhatsApp.';
    }
    if (!isBatch) {
      return 'A mensagem será gerada no horário do envio.';
    }
    return 'Aguardando o horário de envio.';
  }

  if (status === 'processing') {
    return 'Enviando a mensagem agora.';
  }

  if (status === 'sent') {
    return 'Mensagem enviada com sucesso.';
  }

  if (status === 'cancelled') {
    return 'Este agendamento foi cancelado.';
  }

  return '';
}

export function buildUserContentPlaceholder(item) {
  if (!item) return '';

  if (item.content_generation_mode === 'batch') {
    return 'A mensagem será gerada antes do envio.';
  }

  return 'Será gerada no horário do envio.';
}

export function parseRecurrenceFilterValue(value) {
  if (!value) {
    return { recurrencePreset: null, recurrenceDays: null };
  }

  if (value.startsWith(RECURRENCE_DAYS_FILTER_PREFIX)) {
    return {
      recurrencePreset: null,
      recurrenceDays: value.slice(RECURRENCE_DAYS_FILTER_PREFIX.length),
    };
  }

  return { recurrencePreset: value, recurrenceDays: null };
}

export function buildRecurrenceFilterOptions(recurrencePatternOptions = []) {
  const baseOptions = RECURRENCE_PRESET_FILTER_OPTIONS.filter(
    (option) => option.value !== 'custom'
  );
  const dynamicOptions = recurrencePatternOptions.map((pattern) => ({
    value: `${RECURRENCE_DAYS_FILTER_PREFIX}${pattern.value}`,
    label: `${pattern.label} (${pattern.count})`,
  }));
  const customOption = RECURRENCE_PRESET_FILTER_OPTIONS.find(
    (option) => option.value === 'custom'
  );

  if (dynamicOptions.length === 0) {
    return RECURRENCE_PRESET_FILTER_OPTIONS;
  }

  return [...baseOptions, ...dynamicOptions, customOption];
}
